import React, { FC, ReactNode } from 'react';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { MtsButton } from '@mts-ds/components-react';
import { ButtonColors } from '@mts-ds/components/dist/types/components/button/enums';

import { IUpdateOrCreateTemplate } from '../../../../services/NotificationsService';
import { required } from '../../../../utils/validation';
import useRequestHandler from '../../../../hooks/useRequestHandler';

import ContentForm from '../../../../components_/Content/ContentForm';
import FormBase from '../../../../components_/Form';
import FormRow from '../../../../components_/Form/FormRow';
import FormCol from '../../../../components_/Form/FormCol';
import FormInput from '../../../../components_/Form/FormInput';
import FormDropdown from '../../../../components_/Form/FormDropdown';
import FormButton from '../../../../components_/Form/FormButton';
import FormUserList from '../../../../components_/Form/FormUserList';
import FormUserListChip from '../../../../components_/Form/FormUserList/FormUserListChip';

import { IProps as IFormProps } from './';
import FormCard from './FormCard';
import FormTemplate from './FormTemplate';
import FormConformation from './FormConfirmation';
import FormSound from './FormSound';
import { channelSupportsSound } from './channelSound';
import {
  toSeverityOptions,
  severityToFieldValue,
  fieldValueToSeverity,
  describeSeverity,
  withSelectedOption
} from './templateSeverity';

const useStyles = makeStyles(({ spacing }) => ({
  tooltip: {
    width: 365
  },
  switch: {
    marginBottom: 10
  },
  buttons: {
    '& > *:not(:last-child)': {
      marginRight: spacing(4)
    }
  }
}));

type IValues = IUpdateOrCreateTemplate;

type IRender = (props: FormRenderProps<IValues>) => ReactNode;

interface IProps {
  data: IFormProps['data'];
  item: IValues | null;
  isCreate: boolean;
  isEdit: boolean;
  getDataUsers: IFormProps['getDataUsers'];
  onSubmitAction: any;
  onCancel: any;
}

const defaultValues: IValues = {
  id: 0,
  notificationClass: '',
  name: '',
  target: '',
  channelType: '',
  template: '',
  employeesList: [],
  isNeedConfirmation: true,
  isNeedSound: true,
  isNeedVibro: true,
  templateSeverity: null
};

const Form: FC<IProps> = ({
  data,
  item,
  isCreate,
  isEdit,
  getDataUsers,
  onSubmitAction,
  onCancel
}) => {
  const { notificationTypes, users, shortUsers, notificationSeverities } = data;

  const classes = useStyles();
  const requestHandler = useRequestHandler();

  const initialValues: IValues = {
    ...defaultValues,
    ...(isEdit && item),
    // Только в режиме редактирования читаем templateSeverity из загруженного шаблона;
    // в режиме создания берём null (Auto), чтобы устаревший item из store не протёк
    // в новый шаблон (остальные поля изолированы спредом ...(isEdit && item)).
    templateSeverity: severityToFieldValue(isEdit ? item?.templateSeverity : null)
  };

  const notificationTypeOptions = notificationTypes.map(({ class: id, descr }) => ({
    id,
    name: descr
  }));

  const severityOptions = toSeverityOptions(notificationSeverities);

  const submitButtonTitle = isEdit ? 'Сохранить' : 'Добавить';

  const onSubmit = async (values: IValues) => {
    // sentinel «Авто» → null; иначе systemName. На бэкенд уходит только null либо валидный systemName.
    const payload: IValues = {
      ...values,
      templateSeverity: fieldValueToSeverity(values.templateSeverity)
    };
    const error = await onSubmitAction(payload);

    requestHandler({
      error,
      entity: 'notification',
      onSuccess: onCancel,
      isCreate,
      isEdit
    });
  };

  const render: IRender = ({
    handleSubmit,
    values: { notificationClass, target, channelType, templateSeverity }
  }) => {
    // tslint:disable-next-line: no-shadowed-variable
    const { targets = [] } = notificationTypes.find(item => item.class === notificationClass) || {};
    // tslint:disable-next-line: no-shadowed-variable
    const { isList, isReadConfirm, isSound, channels = [] } =
      // tslint:disable-next-line: no-shadowed-variable
      targets.find(item => item.target === target) || {};

    // Звуковой сигнал доступен, если его поддерживает выбранный канал доставки
    // (SUPPORT_SOUND в channelSpecs) — это актуально для веб-канала SignalR, у которого
    // target-флаг isSound=false. isSound оставлен как fallback для прочих получателей.
    const soundSupported = isSound || channelSupportsSound(channels, channelType);

    // tslint:disable-next-line: no-shadowed-variable
    const notificationTargetOptions = targets.map(item => ({
      id: item.target,
      name: item.descr
    }));

    // tslint:disable-next-line: no-shadowed-variable
    const notificationChannelOptions = channels.map(item => ({
      id: item.channelType,
      name: item.friendlyName
    }));

    // Пояснение к выбранной важности: для «Авто» — о наследовании от события,
    // для известного systemName — description из справочника, для неизвестного — пометка.
    const severityDescription = describeSeverity(templateSeverity, notificationSeverities);

    return (
      <FormBase style={{ height: '100%' }} onSubmit={handleSubmit}>
        <ContentForm>
          <Grid>
            <FormCard data={data} />
            <FormRow>
              <FormCol>
                <FormDropdown
                  name="notificationClass"
                  label="Тип события"
                  placeholder="Выберите тип события"
                  options={notificationTypeOptions}
                  useFieldConfig={{ validate: required }}
                />
              </FormCol>
              <FormCol>
                <FormInput
                  name="name"
                  label="Название шаблона"
                  placeholder="Введите название шаблона"
                  useFieldConfig={{ validate: required }}
                />
              </FormCol>
              <FormCol>
                <FormDropdown
                  name="target"
                  label="Получатели уведомления"
                  placeholder="Выберите получателя"
                  disabled={!Boolean(notificationTargetOptions.length)}
                  options={notificationTargetOptions}
                  useFieldConfig={{ validate: required }}
                />
              </FormCol>
              <FormCol>
                <FormDropdown
                  name="channelType"
                  label="Канал уведомления"
                  placeholder="Выберите канал"
                  disabled={!Boolean(notificationChannelOptions.length)}
                  options={notificationChannelOptions}
                  useFieldConfig={{ validate: required }}
                />
              </FormCol>
            </FormRow>
            <FormRow>
              <FormCol>
                <FormDropdown
                  name="templateSeverity"
                  label="Важность"
                  placeholder="Выберите важность"
                  options={withSelectedOption(severityOptions, templateSeverity)}
                  description={severityDescription}
                />
              </FormCol>
            </FormRow>
            <FormRow>
              <FormCol>
                <FormTemplate data={data} />
              </FormCol>
            </FormRow>
            {isList && (
              <FormRow>
                <FormCol xs={4}>
                  <FormUserList
                    title="Добавьте сотрудников, которые получат уведомление"
                    users={users}
                    getUsers={getDataUsers}
                  />
                </FormCol>
                <FormCol>
                  <FormUserListChip shortUsers={shortUsers} />
                </FormCol>
              </FormRow>
            )}
            {isReadConfirm && (
              <FormRow>
                <FormCol>
                  <FormConformation />
                </FormCol>
              </FormRow>
            )}
            {soundSupported && (
              <FormRow>
                <FormCol>
                  <FormSound />
                </FormCol>
              </FormRow>
            )}
          </Grid>
          <Grid container justify="center" className={classes.buttons}>
            <MtsButton color={'negative' as ButtonColors} onClick={onCancel}>
              Отмена
            </MtsButton>
            <FormButton>{submitButtonTitle}</FormButton>
          </Grid>
        </ContentForm>
      </FormBase>
    );
  };

  return <FinalForm initialValues={initialValues} onSubmit={onSubmit} render={render} />;
};

export default Form;
